import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth, API } from "../App";
import axios from "axios";
import { Send, Loader2, LogOut, History, Crown, BarChart3, Brain, Heart, BookOpen, Menu, X, User, Sparkles, Infinity } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import LoadingPhrases from "../components/LoadingPhrases";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { ChatMessage, ProgressBar } from "../components/ConsultaComponents";
import ConsultaResult from "../components/ConsultaResult";
import useConsultation from "../hooks/useConsultation";
import SecurityQuestionPrompt from "../components/SecurityQuestionPrompt";
import AvatarSetup from "../components/AvatarSetup";
import PlanIntentBanner from "../components/PlanIntentBanner";
import { useIsWebView } from "../hooks/useIsWebView";

const ProgressRing = ({ used, limit, color, size = 36, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(used / limit, 1);
  const offset = circumference - progress * circumference;
  return (
    <svg width={size} height={size} className="transform -rotate-90 flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-white/10" />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
    </svg>
  );
};

const SimCounter = ({ user, t, navigate, isWebView }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`);
        setStatus(res.data);
      } catch (e) { console.error("Subscription status fetch failed:", e); }
    };
    fetchStatus();
  }, []);

  if (!status) return null;

  const plan = status.plan;
  const isUnlimited = status.simulations_remaining === "unlimited";
  const remaining = isUnlimited ? null : status.simulations_remaining;
  const used = status.simulations_used || 0;
  const limit = status.simulations_limit;
  const resetType = status.reset_type;

  // Calculate time to next reset
  let resetLabel = "";
  if (status.next_reset && !isUnlimited) {
    const now = new Date();
    const reset = new Date(status.next_reset);
    const diffMs = reset - now;
    if (diffMs > 0) {
      const diffH = Math.floor(diffMs / 3600000);
      if (diffH < 24) {
        resetLabel = `${t("sim_counter_next_reset")} ${t("sim_counter_next_reset_in")} ${diffH}${t("sim_counter_hours")}`;
      } else {
        const diffD = Math.ceil(diffH / 24);
        resetLabel = `${t("sim_counter_next_reset")} ${t("sim_counter_next_reset_in")} ${diffD}d`;
      }
    }
  }

  const periodLabel = resetType === "daily" ? t("sim_counter_daily") : t("sim_counter_monthly");

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-white/5 border border-white/10 rounded-2xl" data-testid="sim-counter">
      {isUnlimited ? (
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <Infinity className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white/90">{t("sim_counter_unlimited")}</p>
            <p className="text-[10px] text-amber-400/70 font-medium uppercase tracking-wide">Avanza</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="relative flex items-center justify-center">
            <ProgressRing used={used} limit={typeof limit === "number" ? limit : 1} color={remaining > 0 ? "#002FA7" : "#EF4444"} size={36} strokeWidth={3} />
            <span className={`absolute text-xs font-bold ${remaining > 0 ? "text-[#002FA7]" : "text-red-400"}`}>{remaining}</span>
          </div>
          <div>
            <p className="text-sm text-white/80">
              <span className="font-medium">{remaining}</span>
              <span className="text-white/50 ml-1">{remaining === 1 ? t("sim_counter_available") : t("sim_counter_available_pl")}</span>
              <span className="text-white/30 ml-1 text-xs">({periodLabel})</span>
            </p>
            {resetLabel && <p className="text-[10px] text-white/30">{resetLabel}</p>}
            {remaining === 0 && plan !== "avanza" && !isWebView && (
              <button onClick={() => navigate("/pricing")} className="text-[10px] text-[#002FA7] hover:underline font-medium" data-testid="sim-upgrade-link">
                {t("sim_counter_upgrade")}
              </button>
            )}
            {remaining === 0 && plan !== "avanza" && isWebView && (
              <span className="text-[10px] text-white/40" data-testid="webview-sub-text-sim">{t("webview_sub_managed")}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ConsultaDirecta = () => {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const { t, lang } = useLanguage();
  const isWebView = useIsWebView();
  const chatEndRef = useRef(null);
  const resultRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSecurityPrompt, setShowSecurityPrompt] = useState(false);
  const [showAvatarSetup, setShowAvatarSetup] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const {
    phase, dilemma, setDilemma, questionNumber, totalQuestions,
    answer, setAnswer, chatHistory, response, loading, error,
    inputRef, handleStartConsultation, handleSubmitAnswer, handleReset,
  } = useConsultation({ lang, t });

  const exampleQuestions = [
    t("example_q1"), t("example_q2"), t("example_q3"),
    t("example_q4"), t("example_q5"), t("example_q6"),
  ];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, phase]);

  useEffect(() => {
    if (phase === "result") {
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [phase]);

  useEffect(() => {
    if (user && !user.has_security_question) {
      setShowSecurityPrompt(true);
    } else if (user && user.has_security_question && !user.avatar_configured) {
      setShowAvatarSetup(true);
    }
  }, [user]);

  // Show limit modal in WebView when simulation limit is reached
  useEffect(() => {
    if (error && error.toLowerCase().includes("limit") && isWebView) {
      setShowLimitModal(true);
    }
  }, [error, isWebView]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const navItems = [
    { key: "history", icon: History, label: t("nav_history"), path: "/history", show: true },
    { key: "pricing", icon: Crown, label: user?.plan === "descubre" ? t("nav_improve") : user?.plan, path: "/pricing", show: !isWebView },
    { key: "compare", icon: BarChart3, label: t("nav_compare"), path: "/comparador", show: user?.plan === "avanza" },
    { key: "wellbeing", icon: Heart, label: t("nav_wellbeing"), path: "/bienestar", show: user?.plan === "decide" || user?.plan === "avanza" },
    { key: "diary", icon: Sparkles, label: t("nav_diary"), path: "/diario", show: user?.plan === "avanza" },
    { key: "library", icon: BookOpen, label: t("nav_library"), path: "/biblioteca", show: user?.plan === "decide" || user?.plan === "avanza" },
  ].filter(i => i.show);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {showSecurityPrompt && (
        <SecurityQuestionPrompt
          onComplete={() => { setShowSecurityPrompt(false); setShowAvatarSetup(true); checkAuth(); }}
          onDismiss={null}
        />
      )}

      {showAvatarSetup && (
        <AvatarSetup
          onComplete={() => { setShowAvatarSetup(false); checkAuth(); }}
        />
      )}

      {/* WebView Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="webview-limit-modal">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center animate-fade-in">
            <div className="w-14 h-14 rounded-full bg-[#002FA7]/15 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-7 h-7 text-[#002FA7]" />
            </div>
            <p className="text-white/80 text-sm leading-relaxed mb-6">{t("webview_limit_modal")}</p>
            <Button onClick={() => setShowLimitModal(false)} className="w-full bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-full py-5 font-medium" data-testid="webview-limit-accept-btn">
              {t("webview_limit_accept")}
            </Button>
          </div>
        </div>
      )}
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] bg-[#002FA7]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="fixed bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#5B21B6]/5 rounded-full blur-[150px] pointer-events-none" />

      {/* Plan intent banner (shown after register?plan=X landed from landing CTA) */}
      <PlanIntentBanner />

      {/* Header */}
      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1 sm:p-1.5">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-lg sm:text-xl tracking-tight">FUTURA</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-2">
            <LanguageSelector variant="dark" />
            {navItems.map((item) => (
              <Button key={item.key} variant="ghost" onClick={() => navigate(item.path)} className="text-white/60 hover:text-white hover:bg-white/5 text-sm" data-testid={`header-${item.key}-btn`}>
                <item.icon className="w-4 h-4 mr-1.5" />{item.label}
              </Button>
            ))}
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate("/perfil")} data-testid="header-profile-link">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center text-sm font-medium">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="text-sm text-white/60">{user?.name?.split(" ")[0]}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white/40 hover:text-white hover:bg-white/5" data-testid="header-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>

          {/* Mobile nav controls */}
          <div className="flex lg:hidden items-center gap-2">
            <LanguageSelector variant="dark" />
            <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate("/perfil")} data-testid="mobile-profile-link">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center text-xs font-medium">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/5 bg-[#0d1020]/95 backdrop-blur-xl animate-fade-in" data-testid="mobile-menu">
            <div className="max-w-5xl mx-auto px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <button key={item.key} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/5 text-sm transition-colors" data-testid={`mobile-${item.key}-btn`}>
                  <item.icon className="w-4 h-4" />{item.label}
                </button>
              ))}
              <button onClick={() => { navigate("/perfil"); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/5 text-sm transition-colors" data-testid="mobile-perfil-btn">
                <User className="w-4 h-4" />{t("nav_profile") || "Perfil"}
              </button>
              <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-red-400/70 hover:text-red-400 hover:bg-red-500/5 text-sm transition-colors" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4" />{t("nav_logout") || "Cerrar sesión"}
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Phase: Input */}
        {phase === "input" && (
          <div className="animate-fade-in">
            <div className="flex justify-center mb-6">
              <SimCounter user={user} t={t} navigate={navigate} isWebView={isWebView} />
            </div>
            <div className="text-center mb-8 sm:mb-16">
              <h1 className="font-['Outfit'] text-2xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 sm:mb-6 leading-tight">
                {t("consulta_h1_1")}
                <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#002FA7] to-[#5B21B6] bg-clip-text text-transparent">{t("consulta_h1_2")}</span>
              </h1>
              <p className="text-sm sm:text-lg text-white/50 max-w-xl mx-auto">{t("consulta_subtitle")}</p>
            </div>
            <div className="mb-8 sm:mb-12">
              <div className="relative">
                <Textarea value={dilemma} onChange={(e) => setDilemma(e.target.value)} placeholder={t("consulta_placeholder")} className="w-full min-h-[100px] sm:min-h-[140px] bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl px-5 sm:px-8 py-4 sm:py-6 text-base sm:text-lg text-white placeholder:text-white/30 focus:border-[#002FA7]/50 focus:ring-2 focus:ring-[#002FA7]/20 resize-none transition-all pb-16" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleStartConsultation(); } }} disabled={loading} data-testid="question-input" />
                <Button onClick={() => handleStartConsultation()} disabled={loading || !dilemma.trim()} className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white rounded-full px-4 sm:px-6 py-2.5 sm:py-3 font-medium text-sm sm:text-base transition-all disabled:opacity-30" data-testid="submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <>{t("consulta_start")}<Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1.5 sm:ml-2" /></>}
                </Button>
              </div>
            </div>
            <div className="mb-8 sm:mb-16">
              <p className="text-center text-white/40 text-xs sm:text-sm mb-4 sm:mb-6">{t("consulta_examples_title")}</p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                {exampleQuestions.map((ex) => (
                  <button key={ex} onClick={() => { setDilemma(ex); handleStartConsultation(ex); }} className="px-3.5 sm:px-5 py-2 sm:py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-full text-xs sm:text-sm text-white/70 hover:text-white transition-all" data-testid={`example-${ex}`}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Phase: Questions */}
        {(phase === "questions" || phase === "loading_result") && (
          <div className="animate-fade-in">
            <ProgressBar current={questionNumber} total={totalQuestions} t={t} />
            <div className="space-y-4 mb-6 max-h-[50vh] overflow-y-auto pr-2">
              {chatHistory.map((msg) => <ChatMessage key={msg.id} msg={msg} />)}
              {loading && phase === "questions" && (
                <div className="flex justify-start animate-fade-in">
                  <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl px-4 sm:px-6 py-3 sm:py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-white/50" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {phase === "questions" && (
              <div className="relative">
                <Textarea ref={inputRef} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t("consulta_answer_placeholder")} className="w-full min-h-[70px] sm:min-h-[80px] bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-white placeholder:text-white/30 focus:border-[#002FA7]/50 focus:ring-2 focus:ring-[#002FA7]/20 resize-none transition-all pr-16 sm:pr-20" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitAnswer(); } }} disabled={loading} data-testid="answer-input" />
                <Button onClick={handleSubmitAnswer} disabled={loading || !answer.trim()} className="absolute bottom-2.5 right-2.5 sm:bottom-3 sm:right-3 bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white rounded-full p-2.5 sm:p-3 disabled:opacity-30" data-testid="send-answer-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
            {phase === "loading_result" && (
              <div className="flex flex-col items-center justify-center py-8 animate-fade-in" data-testid="consulta-loading">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center mb-4 animate-pulse">
                  <Brain className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <p className="text-white/80 text-sm sm:text-base font-medium mb-1" data-testid="consulta-loading-title">{t("consulta_analyzing")}</p>
                <p className="text-white/50 text-xs sm:text-sm text-center max-w-md mb-3" data-testid="consulta-loading-estimate">{t("consulta_loading_estimate")}</p>
                <LoadingPhrases />
              </div>
            )}
          </div>
        )}

        {/* Phase: Result */}
        {phase === "result" && response && (
          <div ref={resultRef}>
            <ConsultaResult response={response} onReset={handleReset} />
          </div>
        )}

        {error && (
          <div className="mb-8 p-4 sm:p-6 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-2xl text-center animate-fade-in" data-testid="error-message">
            <p className="text-[#EF4444] text-sm sm:text-base">{error}</p>
            {error.includes("limit") && !isWebView && (
              <Button onClick={() => navigate("/pricing")} className="mt-4 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full px-6 text-sm">{t("result_view_plans")}</Button>
            )}
            {error.includes("limit") && isWebView && (
              <p className="mt-3 text-white/40 text-sm" data-testid="webview-sub-text-error">{t("webview_sub_managed")}</p>
            )}
          </div>
        )}
      </main>

      <footer className="relative z-10 border-t border-white/5 py-6 sm:py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white/30 text-xs sm:text-sm">{t("consulta_footer")}</p>
        </div>
      </footer>
    </div>
  );
};

export default ConsultaDirecta;
